/**
 * EID-132: PMTiles vector datasources — the read/render side.
 *
 * A PMTiles archive is a single static object (typically on R2/S3) holding a
 * Hilbert-ordered pyramid of MVT tiles. It is read with HTTP **range requests
 * only** — there is no tile server anywhere in this path. This module owns
 * everything that is not deck.gl: resolving the archive's tile scheme, the
 * tile <-> lon/lat math for both schemes, the bounds/zoom gate that keeps
 * out-of-range tiles from ever hitting the network, and the MVT decode.
 *
 * Derived-viz-cache caveat (deliberate, documented, not solved here): the
 * archive is a *display* artefact cooked from the full-fidelity vector source.
 * Geometry is quantised to the MVT tile grid (4096 units per tile by default)
 * and attributes are whatever the cook step encoded, so picked features carry
 * cook-time attributes and tile-local-quantised geometry — not the source
 * record. Consumers that need source fidelity must query the source datasource.
 *
 * ## Tile schemes
 *
 * The PMTiles v3 spec is projection-silent: no CRS or tile-matrix-set field in
 * the header, TileIDs are a plain Hilbert curve over a square 2^z x 2^z grid,
 * and the JSON metadata section is open. Web Mercator is ecosystem convention,
 * not a format guarantee. So the scheme is read from the archive's JSON
 * metadata `tileMatrixSet` key (EID-132 decision, 2026-07-30):
 *
 *   absent | "WebMercatorQuad" -> mercator interpretation (ecosystem default)
 *   "WorldCRS84Quad"           -> geographic quadtree, full +/-90 coverage
 *
 * An unrecognised value is a hard error rather than a silent fallback: a
 * foreign reader will already misplace a non-mercator archive without warning,
 * so this reader refuses to guess. Pair the metadata declaration with a loud
 * filename convention (e.g. `.geo.pmtiles`) when publishing archives.
 *
 * ### Level numbering for WorldCRS84Quad
 *
 * PMTiles TileIDs are only defined over a SQUARE grid (`zxyToTileId` throws for
 * x or y >= 2^z), while WorldCRS84Quad level L is 2^(L+1) columns x 2^L rows.
 * A writer therefore has exactly one legal choice: store WorldCRS84Quad level L
 * at archive level L+1, occupying the upper half (y < 2^L) of the square grid.
 *
 * This module speaks **archive levels** throughout — the z that goes to
 * `getZxy`, and the z in the header's min/max zoom — so there is a single
 * numbering everywhere. At archive level z the geographic grid is
 * 2^z columns x 2^(z-1) rows of tiles that are square in degrees (360/2^z on a
 * side), and archive level z is WorldCRS84Quad level z-1. Archive level 0 is
 * unaddressable in this scheme (it would be half a row).
 *
 * ## Honest limitations
 *
 * - Mercator archives physically cannot contain |lat| > 85.051129 deg; that is
 *   a cook-time exclusion this reader cannot recover.
 * - Globe / first-person / VR views get best-effort rendering of whichever
 *   scheme the archive uses until a view-generic LOD traversal exists (EID-137).
 * - MVT quantisation can show at extreme immersive close-ups.
 */

// The `pmtiles` reader and `@loaders.gl/mvt` are both ESM-only and only needed
// once a spec actually declares a pmtiles datasource, so they are imported
// lazily (EID-63: keep heavyweight deps out of the eager runtime chunk). The
// promises are memoised so repeated tile reads do not re-enter the loader.
let pmtilesModule: Promise<typeof import('pmtiles')> | null = null;
const loadPMTilesModule = () => (pmtilesModule ??= import('pmtiles'));

let mvtModule: Promise<typeof import('@loaders.gl/mvt')> | null = null;
const loadMVTModule = () => (mvtModule ??= import('@loaders.gl/mvt'));

export type TileMatrixSetName = 'WebMercatorQuad' | 'WorldCRS84Quad';

/** Archive-level tile address (see the level-numbering note above). */
export interface TileIndex {
  z: number;
  x: number;
  y: number;
}

/** [west, south, east, north] in degrees. */
export type GeoBounds = [number, number, number, number];

export interface TileMatrixSet {
  name: TileMatrixSetName;
  /** Lowest archive level the scheme can address. */
  minLevel: number;
  /** [columns, rows] of the tile grid at an archive level. */
  gridSize: (z: number) => [number, number];
  /** Geographic extent of one tile. */
  tileBounds: (z: number, x: number, y: number) => GeoBounds;
  /**
   * Tile-local unit coordinates (u, v in [0, 1], v measured downwards from the
   * tile's north edge — the orientation `@loaders.gl/mvt` emits for
   * `coordinates: 'local'`) to [lon, lat].
   */
  project: (
    z: number,
    x: number,
    y: number,
    u: number,
    v: number,
  ) => [number, number];
}

/**
 * The mercator latitude limit. Nothing outside it exists in a WebMercatorQuad
 * archive — the cook step drops it, and no reader can bring it back.
 */
export const MERCATOR_MAX_LATITUDE = 85.0511287798066;

const mercatorLatitude = (t: number) =>
  (Math.atan(Math.sinh(Math.PI * (1 - 2 * t))) * 180) / Math.PI;

const WebMercatorQuad: TileMatrixSet = {
  name: 'WebMercatorQuad',
  minLevel: 0,
  gridSize: (z) => [2 ** z, 2 ** z],
  tileBounds: (z, x, y) => {
    const n = 2 ** z;
    return [
      (x / n) * 360 - 180,
      mercatorLatitude((y + 1) / n),
      ((x + 1) / n) * 360 - 180,
      mercatorLatitude(y / n),
    ];
  },
  project: (z, x, y, u, v) => {
    const n = 2 ** z;
    return [((x + u) / n) * 360 - 180, mercatorLatitude((y + v) / n)];
  },
};

const WorldCRS84Quad: TileMatrixSet = {
  name: 'WorldCRS84Quad',
  // Archive level 0 would be a single 360x360-degree tile, i.e. half a row of
  // the pyramid — not addressable. Level 1 is WorldCRS84Quad level 0 (2x1).
  minLevel: 1,
  gridSize: (z) => [2 ** z, 2 ** (z - 1)],
  tileBounds: (z, x, y) => {
    // Tiles are square in degrees: 360/2^z wide and 180/2^(z-1) = 360/2^z tall.
    const size = 360 / 2 ** z;
    const west = -180 + x * size;
    const north = 90 - y * size;
    return [west, north - size, west + size, north];
  },
  project: (z, x, y, u, v) => {
    // Linear in both axes — this is the whole point of a geographic quadtree:
    // MVT tile-local coordinates decode straight to lon/lat with no projection.
    const size = 360 / 2 ** z;
    return [-180 + (x + u) * size, 90 - (y + v) * size];
  },
};

export const TILE_MATRIX_SETS: Record<TileMatrixSetName, TileMatrixSet> = {
  WebMercatorQuad,
  WorldCRS84Quad,
};

/**
 * Read the tile scheme out of an archive's JSON metadata section. Absent (or
 * explicitly null) means the ecosystem default; an unrecognised value throws
 * rather than silently misplacing every tile.
 */
export const resolveTileMatrixSet = (
  metadata: Record<string, unknown> | null | undefined,
): TileMatrixSet => {
  const declared = metadata?.tileMatrixSet;
  if (declared === undefined || declared === null) return WebMercatorQuad;
  if (typeof declared !== 'string') {
    throw new Error(
      `PMTiles metadata tileMatrixSet must be a string, got ${typeof declared}`,
    );
  }
  const match = (Object.keys(TILE_MATRIX_SETS) as TileMatrixSetName[]).find(
    (name) => name.toLowerCase() === declared.toLowerCase(),
  );
  if (!match) {
    throw new Error(
      `PMTiles metadata declares unsupported tileMatrixSet '${declared}' ` +
        `(supported: ${Object.keys(TILE_MATRIX_SETS).join(', ')})`,
    );
  }
  return TILE_MATRIX_SETS[match];
};

/** Everything the renderer needs to know about an archive before reading tiles. */
export interface PMTilesArchiveInfo {
  tileMatrixSet: TileMatrixSet;
  /** Archive levels that carry tiles, inclusive. */
  minZoom: number;
  maxZoom: number;
  /** Declared data extent; whole-world when the archive declares none. */
  bounds: GeoBounds;
  /** [lon, lat, zoom] suggested view, only meaningful if the archive set it. */
  center: [number, number, number];
  /** PMTiles TileType enum value; 1 is MVT. */
  tileType: number;
  metadata: Record<string, unknown>;
}

/** PMTiles TileType for Mapbox Vector Tiles — the only type this reader decodes. */
export const MVT_TILE_TYPE = 1;

const TILE_TYPE_NAMES: Record<number, string> = {
  0: 'unknown',
  1: 'MVT',
  2: 'PNG',
  3: 'JPEG',
  4: 'WebP',
  5: 'AVIF',
  6: 'MLT',
};

const describeTileType = (tileType: number) =>
  TILE_TYPE_NAMES[tileType] ?? `type ${tileType}`;

const WHOLE_WORLD: GeoBounds = [-180, -90, 180, 90];

/**
 * A header whose bounds are degenerate (the all-zero default a writer leaves
 * when it declares nothing) must not gate every tile out, so read it as
 * "extent unknown — the whole world".
 */
const headerBounds = (header: {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}): GeoBounds =>
  header.minLon >= header.maxLon || header.minLat >= header.maxLat
    ? WHOLE_WORLD
    : [header.minLon, header.minLat, header.maxLon, header.maxLat];

const intersects = (a: GeoBounds, b: GeoBounds) =>
  a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3];

/**
 * Is this tile addressable in this archive at all? Answers without touching
 * the network, so the tiled render path can drop requests deck.gl's traversal
 * would otherwise make for levels the archive does not carry, grid positions
 * that do not exist in the scheme, or areas outside the declared data extent.
 */
export const tileInRange = (
  info: PMTilesArchiveInfo,
  { z, x, y }: TileIndex,
): boolean => {
  if (!Number.isInteger(z) || !Number.isInteger(x) || !Number.isInteger(y))
    return false;
  const scheme = info.tileMatrixSet;
  if (z < Math.max(info.minZoom, scheme.minLevel) || z > info.maxZoom)
    return false;
  const [columns, rows] = scheme.gridSize(z);
  if (x < 0 || x >= columns || y < 0 || y >= rows) return false;
  return intersects(scheme.tileBounds(z, x, y), info.bounds);
};

/** GeoJSON-ish shape emitted by the MVT decode — deck.gl consumes it directly. */
export interface DecodedFeature {
  type: 'Feature';
  geometry: { type: string; coordinates: unknown };
  properties: Record<string, unknown>;
}

const mapPositions = (
  coordinates: unknown,
  project: (position: number[]) => number[],
): unknown => {
  const list = coordinates as unknown[];
  if (!Array.isArray(list) || list.length === 0) return coordinates;
  if (typeof list[0] === 'number') return project(list as number[]);
  return list.map((child) => mapPositions(child, project));
};

/**
 * Decode one MVT tile body into lon/lat GeoJSON features.
 *
 * The tile is decoded in tile-local unit coordinates and projected here rather
 * than letting `@loaders.gl/mvt` do its `wgs84` conversion, because that
 * conversion is hard-wired to Web Mercator — which would silently misplace a
 * WorldCRS84Quad archive. One decode path, two projections.
 */
export const decodeTile = async (
  data: ArrayBuffer,
  scheme: TileMatrixSet,
  { z, x, y }: TileIndex,
): Promise<DecodedFeature[]> => {
  const { MVTLoader } = await loadMVTModule();
  // Fail loudly rather than degrade: the loaders.gl type declares parseSync as
  // optional, and silently treating a missing parser as "this tile is empty"
  // would render every tile blank with nothing in the console.
  if (!MVTLoader.parseSync)
    throw new Error('@loaders.gl/mvt exposes no synchronous MVT parser');
  const features = MVTLoader.parseSync(data, {
    mvt: { shape: 'geojson', coordinates: 'local', layerProperty: 'layerName' },
  }) as DecodedFeature[] | undefined;
  if (!features?.length) return [];
  const project = ([u, v]: number[]) => scheme.project(z, x, y, u, v);
  return features.map((feature) => ({
    ...feature,
    geometry: {
      ...feature.geometry,
      coordinates: mapPositions(feature.geometry?.coordinates, project),
    },
    properties: feature.properties ?? {},
  }));
};

/**
 * Minimal surface this module needs from the `pmtiles` reader — declared so
 * tests (and the deck layer's own tests) can drive the source with a stand-in
 * archive without a range server.
 */
export interface PMTilesArchive {
  getHeader: () => Promise<{
    minZoom: number;
    maxZoom: number;
    minLon: number;
    minLat: number;
    maxLon: number;
    maxLat: number;
    centerZoom: number;
    centerLon: number;
    centerLat: number;
    tileType: number;
  }>;
  getMetadata: () => Promise<unknown>;
  getZxy: (
    z: number,
    x: number,
    y: number,
    signal?: AbortSignal,
  ) => Promise<{ data: ArrayBuffer } | undefined>;
}

/** The `pmtiles` dataSpec, as validated by eidos/data.json. */
export interface PMTilesDataSpec {
  url: string;
  headers?: Record<string, string>;
  forwardAuth?: boolean;
}

/**
 * Compose the headers sent with every range request. Mirrors the zarr
 * datasource's auth surface: declared headers, plus the renderer's bearer
 * token when the spec opts into forwarding it. A rotated token must rebuild
 * the source (the headers are baked into its range-request reader), which the
 * data provider handles by including the token in the entry's snapshot.
 */
export const pmtilesRequestHeaders = (
  dataSpec: PMTilesDataSpec,
  authToken?: string | null,
): Record<string, string> => {
  const headers = { ...(dataSpec.headers ?? {}) };
  if (dataSpec.forwardAuth && authToken)
    headers.Authorization = `Bearer ${authToken}`;
  return headers;
};

export interface PMTilesSourceOptions {
  url: string;
  /** Static request headers; the auth surface mirrors the zarr datasource. */
  headers?: Record<string, string>;
  /** Pre-built archive reader, for tests. Defaults to a range-request reader. */
  archive?: PMTilesArchive;
}

/**
 * A tile-access handle over a PMTiles archive.
 *
 * Deliberately NOT a Dataset: tiles are the vector LOD unit, so consumers get
 * `getTile` plus archive metadata rather than a whole-source load (the vector
 * analogue of the GeoZarr scale-level access in EID-131).
 */
export interface PMTilesSource {
  url: string;
  /** Resolves (once, memoised) the archive header + tile scheme. */
  ready: () => Promise<PMTilesArchiveInfo>;
  /** The resolved info, or null while `ready()` is still in flight. */
  info: () => PMTilesArchiveInfo | null;
  /**
   * Decoded features for one tile, or null when the tile is outside the
   * archive (no request is made) or simply absent from it (empty/deduped).
   */
  getTile: (
    index: TileIndex,
    signal?: AbortSignal,
  ) => Promise<DecodedFeature[] | null>;
}

export const openPMTiles = ({
  url,
  headers,
  archive,
}: PMTilesSourceOptions): PMTilesSource => {
  let resolved: PMTilesArchiveInfo | null = null;
  let archivePromise: Promise<PMTilesArchive> | null = null;
  let infoPromise: Promise<PMTilesArchiveInfo> | null = null;

  const getArchive = () =>
    (archivePromise ??= archive
      ? Promise.resolve(archive)
      : loadPMTilesModule().then(({ PMTiles, FetchSource }) => {
          // FetchSource issues `Range: bytes=a-b` for every read — header,
          // directories and tile bodies alike. Caching is HTTP-level (range
          // responses are cacheable) plus the reader's own directory cache.
          const source = new FetchSource(url, new Headers(headers ?? {}));
          return new PMTiles(source) as unknown as PMTilesArchive;
        }));

  const readInfo = async (): Promise<PMTilesArchiveInfo> => {
    const pmtiles = await getArchive();
    const header = await pmtiles.getHeader();
    const metadata = ((await pmtiles.getMetadata()) ?? {}) as Record<
      string,
      unknown
    >;
    if (header.tileType !== MVT_TILE_TYPE) {
      throw new Error(
        `PMTiles archive ${url} holds ${describeTileType(header.tileType)} ` +
          `tiles; the pmtiles datasource reads vector (MVT) archives only`,
      );
    }
    resolved = {
      tileMatrixSet: resolveTileMatrixSet(metadata),
      minZoom: header.minZoom,
      maxZoom: header.maxZoom,
      bounds: headerBounds(header),
      center: [header.centerLon, header.centerLat, header.centerZoom],
      tileType: header.tileType,
      metadata,
    };
    return resolved;
  };

  const ready = () =>
    // Success is memoised (one header read per source, which the render path
    // relies on); a FAILURE is not. Memoising the rejection would let a single
    // 503 while the header was in flight brick the datasource for the rest of
    // the session, with no way back short of a spec edit. The reader's own
    // SharedPromiseCache memoises the failed header read too, so the archive
    // instance is dropped with it — a retry builds a fresh one.
    (infoPromise ??= readInfo().catch((error) => {
      infoPromise = null;
      archivePromise = null;
      throw error;
    }));

  const getTile = async (index: TileIndex, signal?: AbortSignal) => {
    const info = await ready();
    // The gate is the point: an out-of-range index returns here, before the
    // archive reader is asked for anything.
    if (!tileInRange(info, index)) return null;
    const pmtiles = await getArchive();
    const tile = await pmtiles.getZxy(index.z, index.x, index.y, signal);
    if (!tile?.data) return null;
    return decodeTile(tile.data, info.tileMatrixSet, index);
  };

  return { url, ready, info: () => resolved, getTile };
};
